import React, { CSSProperties, useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { Auth } from 'aws-amplify';
import { useRequest } from 'ahooks';
import axios from 'axios';
import dayjs from 'dayjs';
import { Button, Card, Result, Table, Tag, Tooltip, Typography } from 'antd';
import { FilePdfOutlined, InfoCircleOutlined } from '@ant-design/icons';

import { UserContext } from '../contexts/UserContext';
import { ReactComponent as StripeLogo } from '../images/stripe.svg';

type Invoice = {
  id: string;
  invoice_number: string | null;
  invoice_date: string | null;
  total_amount: number | null;
  status: 'paid' | 'unpaid' | 'void' | null;
  summary_url: string | null;
  details_url: string | null;
  details_filename: string | null;
};

const pageStyle: CSSProperties = {
  margin: '0 auto',
  maxWidth: 1440,
};

const contentCardStyle: CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
  marginTop: 16,
};

const statusTagStyle: CSSProperties = {
  borderRadius: 16,
  fontSize: 14,
  margin: 0,
  padding: '2px 14px',
};

// Shrink a column to its widest entry; the final column takes the remaining width.
const fitToContent = {
  width: 1,
  onCell: () => ({ style: { whiteSpace: 'nowrap' as const } }),
  onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' as const } }),
};

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const STATUS_COLORS: Record<string, string> = {
  paid: 'green',
  unpaid: 'red',
  void: 'default',
};

const StatusTag: React.FC<{ status: Invoice['status'] }> = ({ status }) =>
  status ? (
    <Tag color={STATUS_COLORS[status]} style={statusTagStyle}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Tag>
  ) : null;

const ClientInvoicesPage: React.FC = () => {
  const { user } = useContext(UserContext);
  const { data, loading, error } = useRequest(async () => {
    const token = (await Auth.currentSession()).getIdToken().getJwtToken();
    const results = await axios.get<{ invoices: Invoice[] }>(
      `${process.env.REACT_APP_CHALICE_URL}/invoices`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Hasura-Role': user?.role,
        },
      }
    );
    return results.data.invoices;
  });

  return (
    <>
      <Helmet>
        <title>Billing | Mabel</title>
      </Helmet>
      <div style={pageStyle}>
        <Typography.Title level={2} style={{ marginBottom: 4 }}>
          Billing
        </Typography.Title>
        <Typography.Text type="secondary">
          Invoices will appear here starting October 2026
        </Typography.Text>
        {error ? (
          <Result
            status="error"
            title="There was an error retrieving your invoices"
            subTitle={error.message}
          />
        ) : (
          <Card bodyStyle={{ padding: 0 }} style={contentCardStyle}>
            <Table<Invoice>
              rowKey="id"
              loading={loading}
              dataSource={data || []}
              pagination={false}
              locale={{ emptyText: 'No invoices yet' }}
              columns={[
                {
                  title: (
                    <>
                      Status{' '}
                      <Tooltip
                        title="Payment status syncs daily, so it may not update immediately after you pay."
                        trigger={['hover', 'focus', 'click']}
                      >
                        <button
                          type="button"
                          aria-label="About payment status updates"
                          style={{
                            background: 'none',
                            border: 0,
                            padding: 0,
                            color: 'inherit',
                            cursor: 'help',
                          }}
                        >
                          <InfoCircleOutlined />
                        </button>
                      </Tooltip>
                    </>
                  ),
                  dataIndex: 'status',
                  align: 'center',
                  width: 140,
                  render: (status: Invoice['status']) => (
                    <StatusTag status={status} />
                  ),
                },
                {
                  title: 'Date',
                  dataIndex: 'invoice_date',
                  ...fitToContent,
                  render: (date: string | null) =>
                    date ? dayjs(date).format('MMMM D, YYYY') : '',
                },
                {
                  title: 'Invoice #',
                  dataIndex: 'invoice_number',
                  ...fitToContent,
                },
                {
                  title: 'Amount',
                  dataIndex: 'total_amount',
                  align: 'right',
                  ...fitToContent,
                  render: (amount: number | null) =>
                    amount == null ? '' : currencyFormatter.format(amount),
                },
                {
                  title: 'Payment',
                  key: 'payment',
                  ...fitToContent,
                  render: (_, invoice) => (
                    <Typography.Link
                      href={invoice.summary_url || undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      disabled={!invoice.summary_url}
                    >
                      <StripeLogo
                        width={16}
                        height={16}
                        aria-hidden="true"
                        focusable="false"
                        style={{ marginRight: 6, verticalAlign: 'middle' }}
                      />
                      Stripe payment link
                    </Typography.Link>
                  ),
                },
                {
                  title: 'Invoice PDF',
                  key: 'details',
                  render: (_, invoice) => (
                    <Button
                      shape="round"
                      href={invoice.details_url || undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      disabled={!invoice.details_url}
                      icon={<FilePdfOutlined />}
                    >
                      Detailed Invoice
                    </Button>
                  ),
                },
              ]}
            />
          </Card>
        )}
      </div>
    </>
  );
};

export default ClientInvoicesPage;
